/* eslint-disable @typescript-eslint/no-namespace */
// logger.ts
// electron-log 仅支持 Electron/Node，在 Safari 等纯浏览器中可能报错导致白屏
// 非 Electron 环境使用基于 console 的轻量 logger
type LoggerLike = {
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  verbose: (...args: unknown[]) => void;
  silly: (...args: unknown[]) => void;
  transports?: { console?: { level: string | false } };
};

function createBrowserLogger(): LoggerLike {
  type LogLevel =
    | "error"
    | "warn"
    | "info"
    | "verbose"
    | "debug"
    | "silly"
    | false;
  let level: LogLevel = "silly";
  const levels: LogLevel[] = [
    "error",
    "warn",
    "info",
    "verbose",
    "debug",
    "silly",
  ];
  const makeLog =
    (method: "error" | "warn" | "info" | "verbose" | "debug" | "silly") =>
    (...args: unknown[]) => {
      const idx = levels.indexOf(method);
      if (level !== false && idx >= 0 && levels.indexOf(level) >= idx) {
        const fn: (...a: unknown[]) => void =
          method === "verbose" || method === "silly"
            ? (console.debug || console.log).bind(console)
            : (
                (console[method as keyof Console] as (
                  ...a: unknown[]
                ) => void) || console.log
              ).bind(console);
        fn(...args);
      }
    };
  const logger: LoggerLike = {
    info: makeLog("info"),
    warn: makeLog("warn"),
    error: makeLog("error"),
    debug: makeLog("debug"),
    verbose: makeLog("verbose"),
    silly: makeLog("silly"),
    transports: {
      console: {
        get level() {
          return level;
        },
        set level(v: LogLevel) {
          level = v;
        },
      },
    },
  };
  return logger;
}

let logger: LoggerLike;
if (process.env.IS_ELECTRON === "true") {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  logger = require("electron-log/renderer").default;
} else {
  logger = createBrowserLogger();
}

declare global {
  const log: LoggerLike;
  interface Window {
    log: LoggerLike;
  }
}

// Safari 等浏览器中 global 可能未定义，webpack 默认不提供 global polyfill
// 将 log 挂到 globalThis，并 polyfill global 指向 globalThis
const g = typeof globalThis !== "undefined" ? globalThis : window;
(g as unknown as { log?: LoggerLike }).log = logger;
if (typeof (g as unknown as { global?: unknown }).global === "undefined") {
  (g as unknown as { global: typeof g }).global = g;
}
window.log = logger;

// 统一忽略浏览器自身的 ResizeObserver 循环警告，避免被当成运行时错误
// 典型报错文案： "ResizeObserver loop completed with undelivered notifications."
// 参考：Chrome 已知问题，通常对功能没有影响
window.addEventListener(
  "error",
  (event) => {
    const msg = event.message || "";
    if (
      msg === "ResizeObserver loop completed with undelivered notifications." ||
      msg === "ResizeObserver loop limit exceeded"
    ) {
      event.stopImmediatePropagation();
    }
  },
  true
);

// 支持通过 LOG_LEVEL 环境变量控制日志级别
// 可选值: error, warn, info, verbose, debug, silly, false
const logLevel = process.env.LOG_LEVEL;

type LogLevel =
  | "error"
  | "warn"
  | "info"
  | "verbose"
  | "debug"
  | "silly"
  | false;

function parseLogLevel(value: string): LogLevel {
  return value === "false" ? false : (value as Exclude<LogLevel, false>);
}

if (logger.transports?.console) {
  if (logLevel) {
    logger.transports.console.level = parseLogLevel(logLevel);
  } else if (
    process.env.VUE_APP_ENV === "production" ||
    process.env.VUE_APP_ENV === "test"
  ) {
    logger.transports.console.level = false;
  } else {
    logger.transports.console.level = "silly";
  }
}

window.onerror = (message, source, lineno, colno, error) => {
  logger.error("[window.onerror]", {
    message: message,
    source: source,
    lineno: lineno,
    colno: colno,
    error: error,
  });
};

document.addEventListener("click", function (event) {
  const dom = event.target as HTMLElement;
  if (!dom) {
    return;
  }
  const clickEvent = {
    timestamp: new Date().toISOString(),
    id: dom.id,
    tagName: dom.tagName,
    classList: Array.from(dom.classList),
    innerText: dom.innerText,
    x: event.clientX,
    y: event.clientY,
  };
  log.info("[click]", clickEvent);
});

export default logger;
